import { useMutation, useQuery } from "@tanstack/react-query";
import {
  invalidateLeadAssignment,
  invalidateLeadTimeline,
  queryClient,
} from "@/lib/queryClient";
import axiosInstance from "@/lib/axios";
import { assignmentRequestEndpoint } from "@/config/endpoints";
import { patchAlertRequest } from "@/features/alerts/api/useAlerts";
import type { AssignmentRequestSummary } from "@/features/alerts/types";

/**
 * "Please assign this lead" (`/core/assignment-requests`): someone who cannot
 * assign — or wants a role above them to decide — asks one person or a role.
 * It travels as an ANNOUNCE alert that keeps popping up for its recipients
 * until Done, an assignment on the lead, or the requester's cancel.
 */
export const ASSIGNMENT_REQUESTS_KEY = ["assignment-requests"] as const;

/** The server's own words for a failed request call, else `fallback`. */
export function assignmentRequestErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
}

export interface AssignmentRequestOption {
  id: number;
  name: string | null;
  roleName?: string | null;
}

export interface AssignmentRequestOptions {
  lead: { id: number; name: string; dealId: number | null; dealName: string | null };
  /** Roles with someone who can assign at this level; `userCount` = who would be asked. */
  roles: Array<{ id: number; name: string; hierarchy: number; userCount: number }>;
  /** People who can assign at this level. */
  people: AssignmentRequestOption[];
  /** Anyone the requester may suggest. */
  assignees: AssignmentRequestOption[];
  pending: AssignmentRequestSummary[];
}

export function useAssignmentRequestOptions(
  leadId: number | null,
  childId: number | null,
  enabled: boolean,
) {
  return useQuery<AssignmentRequestOptions>({
    queryKey: [...ASSIGNMENT_REQUESTS_KEY, "options", leadId, childId],
    queryFn: async () => {
      const params: Record<string, string> = { leadId: String(leadId) };
      if (childId) params.childId = String(childId);
      const { data } = await axiosInstance.get(`${assignmentRequestEndpoint}/options`, { params });
      return {
        lead: data?.lead,
        roles: Array.isArray(data?.roles) ? data.roles : [],
        people: Array.isArray(data?.people) ? data.people : [],
        assignees: Array.isArray(data?.assignees) ? data.assignees : [],
        pending: Array.isArray(data?.pending) ? data.pending : [],
      };
    },
    staleTime: 60 * 1000,
    enabled: enabled && !!leadId,
  });
}

/** One open request on a lead, with whether the caller may press Done. */
export interface PendingAssignmentRequest extends AssignmentRequestSummary {
  forMe: boolean;
}

/** The lead's open requests — the drawer's "asked …" chip. */
export function usePendingAssignmentRequests(leadId: number | null) {
  return useQuery<PendingAssignmentRequest[]>({
    queryKey: [...ASSIGNMENT_REQUESTS_KEY, "lead", leadId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(assignmentRequestEndpoint, {
        params: { leadId: String(leadId) },
      });
      return Array.isArray(data?.requests) ? data.requests : [];
    },
    staleTime: 60 * 1000,
    enabled: !!leadId,
  });
}

export interface CreateAssignmentRequestInput {
  leadId: number;
  childId?: number | null;
  targetUserId?: number | null;
  targetRoleId?: number | null;
  suggestedUserId?: number | null;
  note?: string | null;
}

export interface CreateAssignmentRequestResult {
  request: AssignmentRequestSummary;
  recipientCount: number;
  sample: string[];
}

export function useCreateAssignmentRequest() {
  return useMutation({
    mutationFn: async (input: CreateAssignmentRequestInput) => {
      const { data } = await axiosInstance.post(assignmentRequestEndpoint, input);
      return data as CreateAssignmentRequestResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSIGNMENT_REQUESTS_KEY });
    },
  });
}

export type AssignmentRequestAction = "done" | "cancel" | "assign";

/**
 * Assign (a recipient: the real assignment to the suggested person, or to
 * `userId`), done (a recipient: handled without assigning) or cancel (the
 * requester). Patches the alert it rides; an assignment also refreshes the
 * lead's assignees everywhere.
 */
export function useResolveAssignmentRequest() {
  return useMutation({
    mutationFn: async (input: {
      id: number;
      action: AssignmentRequestAction;
      alertId?: number | null;
      userId?: number | null;
    }) => {
      const { data } = await axiosInstance.patch(
        `${assignmentRequestEndpoint}/${input.id}/${input.action}`,
        input.action === "assign" && input.userId ? { userId: input.userId } : {},
      );
      return data?.request as AssignmentRequestSummary;
    },
    onSuccess: (request, input) => {
      if (request) patchAlertRequest(request.alertId ?? input.alertId ?? null, request);
      else queryClient.invalidateQueries({ queryKey: ASSIGNMENT_REQUESTS_KEY });
      if (input.action === "assign") {
        invalidateLeadAssignment();
        invalidateLeadTimeline();
      }
    },
  });
}
